import { motion, useInView, type HTMLMotionProps } from 'framer-motion'
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  type PropsWithChildren,
} from 'react'

/** Seconds between one step and the next. */
const STEP = 0.09

/** Seconds until a place in the sequence is due. */
type Sequence = (order: number) => number

const SequenceContext = createContext<Sequence>(() => 0)

/**
 * A page's opening sequence.
 *
 * Each part inside it has a place, and is due that many steps after the first
 * of them showed. So the page arrives top to bottom as one movement, and a part
 * that mounts later -- a tab opened, a filter changed -- is not kept waiting
 * for a turn that has long passed.
 */
export function RevealSequence({ children }: PropsWithChildren) {
  const startedAt = useRef<number | null>(null)

  const due = useCallback<Sequence>((order) => {
    const now = performance.now()

    startedAt.current ??= now

    return Math.max(0, order * STEP - (now - startedAt.current) / 1000)
  }, [])

  return <SequenceContext.Provider value={due}>{children}</SequenceContext.Provider>
}

export function useSequence(): Sequence {
  return useContext(SequenceContext)
}

interface RevealProps extends Omit<HTMLMotionProps<'div'>, 'children'> {
  /** Its place in the page's opening sequence. */
  order?: number
  /** Staggers siblings that share a place, whenever they appear. */
  index?: number
}

/**
 * Reveals its children once, when they first come into view.
 *
 * MotionConfig in the layout honours the viewer's reduced-motion setting, so
 * nothing here needs to check for it.
 */
export function Reveal({
  order = 0,
  index = 0,
  children,
  ...props
}: PropsWithChildren<RevealProps>) {
  const node = useRef<HTMLDivElement>(null)
  const isInView = useInView(node, { once: true, amount: 0.15 })
  const due = useSequence()

  /* Worked out as it comes into view: scrolled to later, its turn has passed. */
  const delay = useMemo(() => (isInView ? due(order) + index * STEP : 0), [isInView])

  return (
    <motion.div
      ref={node}
      initial={{ opacity: 0, y: 16 }}
      animate={isInView ? { opacity: 1, y: 0 } : undefined}
      transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }}
      {...props}
    >
      {children}
    </motion.div>
  )
}
